/**
 * Hero セクション。4 variants:
 *   classic — 中央寄せ画像 → タイトル → サブタイトル
 *   split   — 左画像 + 右テキスト (50/50)
 *   minimal — テキストのみ (余白大、画像なし)
 *   banner  — フルブリード横長画像 + オーバーレイテキスト
 */
import { LayoutTemplate } from 'lucide-react';

import { ImageField, TextField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface HeroConfig {
  title: string;
  subtitle: string;
  imageUrl: string;
}

function read(config: Record<string, unknown>): HeroConfig {
  return {
    title: (config.title as string) ?? '',
    subtitle: (config.subtitle as string) ?? '',
    imageUrl: (config.imageUrl as string) ?? '',
  };
}

function HeroEditor({ config, onChange, onCommit }: EditorProps) {
  const v = read(config);
  const set = (patch: Partial<HeroConfig>) => onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="タイトル"
        value={v.title}
        onChange={(x) => set({ title: x })}
        onCommit={onCommit}
        placeholder="次世代の体験を、あなたへ"
      />
      <TextField
        label="サブタイトル"
        value={v.subtitle}
        onChange={(x) => set({ subtitle: x })}
        onCommit={onCommit}
        placeholder="（補足説明）"
      />
      <ImageField
        label="画像"
        value={v.imageUrl}
        onChange={(x) => {
          set({ imageUrl: x });
          onCommit();
        }}
      />
    </div>
  );
}

function HeroClassic({ config }: RendererProps) {
  const v = read(config);
  return (
    <section className="text-center py-12">
      {v.imageUrl && (
        <img
          src={v.imageUrl}
          alt=""
          className="mx-auto mb-6 max-h-80 object-contain"
        />
      )}
      {v.title && (
        <h2 className="text-3xl md:text-4xl font-bold mb-2">{v.title}</h2>
      )}
      {v.subtitle && (
        <p className="text-base md:text-lg text-[var(--text-secondary)]">
          {v.subtitle}
        </p>
      )}
    </section>
  );
}

function HeroSplit({ config }: RendererProps) {
  const v = read(config);
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center py-10">
      <div className="order-2 md:order-1">
        {v.title && (
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{v.title}</h2>
        )}
        {v.subtitle && (
          <p className="text-base text-[var(--text-secondary)] leading-relaxed">
            {v.subtitle}
          </p>
        )}
      </div>
      <div className="order-1 md:order-2">
        {v.imageUrl ? (
          <img
            src={v.imageUrl}
            alt=""
            className="w-full rounded-lg object-cover aspect-[4/3]"
          />
        ) : (
          <div className="w-full aspect-[4/3] bg-[var(--bg-hover)] rounded-lg" />
        )}
      </div>
    </section>
  );
}

function HeroMinimal({ config }: RendererProps) {
  const v = read(config);
  return (
    <section className="text-center py-20 px-4">
      {v.title && (
        <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
          {v.title}
        </h2>
      )}
      {v.subtitle && (
        <p className="text-base md:text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
          {v.subtitle}
        </p>
      )}
    </section>
  );
}

function HeroBanner({ config }: RendererProps) {
  const v = read(config);
  return (
    <section
      className="relative w-full aspect-[16/7] overflow-hidden rounded-lg"
      style={{
        backgroundImage: v.imageUrl ? `url(${v.imageUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: v.imageUrl ? undefined : '#1A1E25',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/20" />
      <div className="relative h-full flex items-center px-8 md:px-16">
        <div className="max-w-2xl text-white">
          {v.title && (
            <h2 className="text-3xl md:text-5xl font-bold mb-3 drop-shadow">
              {v.title}
            </h2>
          )}
          {v.subtitle && (
            <p className="text-base md:text-lg drop-shadow opacity-95">
              {v.subtitle}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export const heroDefinition: SectionDefinition = {
  type: 'hero',
  label: 'ヒーロー',
  icon: LayoutTemplate,
  description: '資料の冒頭に置くアイキャッチ。タイトル + 画像。',
  defaultVariant: 'classic',
  useCases: ['all'],
  variants: {
    classic: {
      label: 'クラシック',
      description: '中央寄せ画像 → タイトル → サブタイトル',
      Renderer: HeroClassic,
      Editor: HeroEditor,
      sample: () => ({
        title: '次世代の体験を、あなたへ',
        subtitle: '高品質と快適さを両立する、新しい選択。',
        imageUrl: '',
      }),
    },
    split: {
      label: 'スプリット',
      description: '左画像 + 右テキスト (50/50)',
      Renderer: HeroSplit,
      Editor: HeroEditor,
      sample: () => ({
        title: '機能と美しさの調和',
        subtitle: '日々の業務に溶け込み、生産性を底上げします。',
        imageUrl: '',
      }),
    },
    minimal: {
      label: 'ミニマル',
      description: 'テキストのみ・余白大。シンプルに訴求',
      Renderer: HeroMinimal,
      Editor: HeroEditor,
      sample: () => ({
        title: 'シンプルな解決策。',
        subtitle: '余計なものは、すべて削ぎ落としました。',
        imageUrl: '',
      }),
    },
    banner: {
      label: 'バナー',
      description: 'フル幅画像 + オーバーレイテキスト',
      Renderer: HeroBanner,
      Editor: HeroEditor,
      sample: () => ({
        title: 'BUSINESS, REIMAGINED.',
        subtitle: '導入から運用まで、ワンストップで。',
        imageUrl: '',
      }),
    },
  },
};
